/**
 * Enhanced Error Boundary Component
 * 
 * Provides comprehensive error handling with user-friendly messages,
 * retry mechanisms, and error reporting
 */

import React, { Component, ErrorInfo, ReactNode, useState } from 'react';
import { View, Pressable, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Heading, Body, Caption } from '@/components/ui/Text';
import { logger } from '@/services/logger';
import { captureException } from '@/services/errorTracking';
import { useTripDetectionSessionStore } from '@/state/tripDetectionSessionStore';
import { clearInboxOauthMarkers } from '@/utils/inboxOauth';

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
};

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  maxRetries?: number;
  resetKeys?: Array<string | number>;
  name?: string;
};

const isFirestoreInternalAssertionError = (error: unknown): boolean => {
  const message = String(
    (error as { message?: string } | null)?.message ?? error ?? '',
  ).toUpperCase();
  return (
    message.includes('FIRESTORE') &&
    message.includes('INTERNAL ASSERTION FAILED') &&
    message.includes('UNEXPECTED STATE')
  );
};

/** User-safe copy — raw React/Firebase messages stay in logs and dev-only details. */
export function sanitizeUserFacingErrorMessage(error: Error | null | undefined): string {
  const raw = error?.message?.trim() || 'An unexpected error occurred';
  const lower = raw.toLowerCase();

  if (lower.includes('maximum update depth exceeded')) {
    return 'This section hit a temporary glitch. Try again, or close and reopen it.';
  }
  if (
    lower.includes('network request failed') ||
    lower.includes('failed to fetch') ||
    lower.includes('network error')
  ) {
    return "We couldn't reach the server. Check your connection and try again.";
  }
  if (!__DEV__) {
    if (
      lower.includes('is not a function') ||
      lower.includes('undefined is not') ||
      lower.includes('cannot read prop') ||
      lower.includes('invalid hook call')
    ) {
      return 'Something unexpected happened. Try again, or restart the app if the problem continues.';
    }
  }

  return raw;
}

export class TripErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private resetTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { name, onError } = this.props;
    const isFirestoreTransient = isFirestoreInternalAssertionError(error);

    // Firestore's SDK-level internal assertion can be transient on React Native;
    // auto-recover quickly and do not trap users on a blocking crash card.
    if (isFirestoreTransient) {
      logger.warn(
        `[ErrorBoundary] ${name || 'Unnamed'} caught transient Firestore assertion; auto-recovering`,
        {
          errorBoundary: name,
          message: error.message,
        },
      );
      if (this.resetTimeoutId) {
        clearTimeout(this.resetTimeoutId);
      }
      this.resetTimeoutId = setTimeout(() => {
        this.resetErrorBoundary(true);
      }, 120);
      return;
    }

    logger.error(
      `[ErrorBoundary] ${name || 'Unnamed'} caught error`,
      error,
      {
        componentStack: errorInfo.componentStack,
        errorBoundary: name,
      }
    );

    captureException(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: name ?? 'Unnamed',
    });

    this.setState({
      error,
      errorInfo,
    });

    onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetKeys } = this.props;
    const { hasError } = this.state;

    // Reset error boundary when resetKeys change
    if (hasError && resetKeys && prevProps.resetKeys) {
      const hasResetKeyChanged = resetKeys.some(
        (key, index) => key !== prevProps.resetKeys?.[index]
      );

      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
      }
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  resetErrorBoundary = (force = false) => {
    const { maxRetries = 3, name } = this.props;
    const { retryCount } = this.state;

    if (!force && retryCount >= maxRetries) {
      logger.warn('[ErrorBoundary] Max retries reached, not resetting');
      return;
    }

    // Root-level boundaries remount the entire navigation tree; clear transient
    // UI state (trip detection session, inbox OAuth markers) and navigate home so
    // the user doesn't resume on a half-crashed screen.
    if (name === 'RootApp') {
      try {
        useTripDetectionSessionStore.getState().clearSession();
      } catch {
        // store may be unavailable during very early boot; ignore
      }
      void clearInboxOauthMarkers().catch(() => {});
      try {
        if (router.canDismiss()) {
          try {
            router.dismissAll();
          } catch {
            router.dismissTo('/(tabs)');
          }
        } else {
          router.replace('/(tabs)');
        }
      } catch {
        try {
          router.dismissTo('/(tabs)');
        } catch {
          try {
            router.replace('/(tabs)');
          } catch {
            /* navigation may be unavailable during early boot */
          }
        }
      }
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: force ? retryCount : retryCount + 1,
    });

    logger.info('[ErrorBoundary] Error boundary reset', {
      retryCount: force ? retryCount : retryCount + 1,
      name,
      force,
    });
  };

  handleRetry = () => {
    this.resetErrorBoundary();
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, name } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return <ErrorFallback 
        error={error} 
        errorInfo={errorInfo}
        onRetry={this.handleRetry}
        componentName={name}
      />;
    }

    return children;
  }
}

type ErrorFallbackProps = {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onRetry: () => void;
  componentName?: string;
};

function ErrorFallback({ error, errorInfo, onRetry, componentName }: ErrorFallbackProps) {
  // Note: This is a functional component, but we can't use hooks in class component context
  // We'll use a wrapper component that can use hooks
  return <ErrorFallbackContent error={error} errorInfo={errorInfo} onRetry={onRetry} componentName={componentName} />;
}

function ErrorFallbackContent({ error, errorInfo, onRetry, componentName }: ErrorFallbackProps) {
  // This can use hooks since it's a separate component
  const theme = useTheme();
  const [showDetails, setShowDetails] = useState(false);

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <Card style={styles.card}>
        <View style={styles.iconContainer}>
          <Ionicons name="alert-circle" size={48} color={theme.colors.error || '#FF3B30'} />
        </View>

        <Heading variant="h3" style={[styles.title, { color: theme.colors.error || '#FF3B30' }]}>
          Something went wrong
        </Heading>

        {componentName && __DEV__ && (
          <Caption color={theme.colors.textSecondary} style={styles.componentName}>
            Component: {componentName}
          </Caption>
        )}

        <Body color={theme.colors.textSecondary} style={styles.message}>
          {sanitizeUserFacingErrorMessage(error)}
        </Body>

        <View style={styles.actions}>
          <Button
            label="Try Again"
            onPress={onRetry}
            variant="primary"
            style={styles.retryButton}
          />
          
          {__DEV__ && (
            <Pressable
              onPress={() => setShowDetails(!showDetails)}
              style={styles.detailsButton}
            >
              <Body color={theme.colors.primary}>
                {showDetails ? 'Hide' : 'Show'} Details
              </Body>
            </Pressable>
          )}
        </View>

        {showDetails && errorInfo && __DEV__ && (
          <View style={[styles.detailsContainer, { backgroundColor: theme.colors.surface }]}>
            <Caption color={theme.colors.textSecondary} style={styles.detailsTitle}>
              Error Details:
            </Caption>
            <Caption color={theme.colors.textSecondary} style={styles.detailsText}>
              {error?.message}
              {'\n\n'}
              {error?.stack}
            </Caption>
            <Caption color={theme.colors.textSecondary} style={styles.detailsTitle}>
              Component Stack:
            </Caption>
            <Caption color={theme.colors.textSecondary} style={styles.detailsText}>
              {errorInfo.componentStack}
            </Caption>
          </View>
        )}
      </Card>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 400,
    padding: 24,
    alignItems: 'center',
  },
  iconContainer: {
    marginBottom: 16,
  },
  title: {
    marginBottom: 8,
    textAlign: 'center',
  },
  componentName: {
    marginBottom: 16,
    fontSize: 12,
  },
  message: {
    marginBottom: 24,
    textAlign: 'center',
  },
  actions: {
    width: '100%',
    gap: 12,
    alignItems: 'center',
  },
  retryButton: {
    width: '100%',
  },
  detailsButton: {
    padding: 8,
  },
  detailsContainer: {
    width: '100%',
    marginTop: 16,
    padding: 12,
    borderRadius: 8,
    maxHeight: 200,
  },
  detailsTitle: {
    fontWeight: '600',
    marginTop: 8,
    marginBottom: 4,
  },
  detailsText: {
    fontSize: 10,
    fontFamily: 'monospace',
  },
});

/**
 * Convenience HOC for wrapping a component in a named TripErrorBoundary.
 *
 *     export default withErrorBoundary(MyFeature, 'MyFeature');
 */
export function withErrorBoundary<P extends object>(
  Wrapped: React.ComponentType<P>,
  name: string,
  options?: { fallback?: ReactNode; maxRetries?: number },
): React.ComponentType<P> {
  const WithBoundary = (props: P) => (
    <TripErrorBoundary
      name={name}
      fallback={options?.fallback}
      maxRetries={options?.maxRetries}
    >
      <Wrapped {...props} />
    </TripErrorBoundary>
  );
  WithBoundary.displayName = `withErrorBoundary(${name})`;
  return WithBoundary;
}

